import z from 'zod';

/** Disabled flags are omitted; when present, the value is an object (often empty). */
const presenceFeatureFlag = z.record(z.string(), z.unknown()).optional();

export const featureFlagsSchema = z
    .object({
        cyberpunk_2077_mission_help: presenceFeatureFlag,
        elden_ring_boss_guide: z
            .object({
                bossDetectionEnabled: z.boolean().optional(),
            })
            .optional(),
        partner_rewards: presenceFeatureFlag,
        daily_lootbox: presenceFeatureFlag,
        featurebase_feedback: z
            .object({
                generalBoardId: z.string().optional(),
            })
            .optional(),
        steam_account_connection_fake_door: presenceFeatureFlag,
        desktop_dd_rum: z
            .object({
                reportRate: z.number().optional(),
            })
            .optional(),
        desktop_dd_tophat_telemetry: z
            .object({
                reportRate: z.number().optional(),
            })
            .optional(),
        // Single kill switch for ALL desktop-driven minidump activity: the
        // on-demand aux captures (wedge/launch + manual) AND crash-dump
        // collection/upload. Separate from desktop_dd_tophat_telemetry so
        // minidumps toggle independently of tophat telemetry. The native
        // fatal-crash handler is NOT gated by this — it always writes on a real
        // crash; this only governs desktop collection/upload and on-demand aux
        // dump requests. Presence = enabled (the desktop_* convention).
        desktop_minidumps: presenceFeatureFlag,
        // `perSurface` absent = enabled; `false` trims the daily report down to
        // the whole-app fields and stops the per-segment sibling event.
        // `hardwareSurvey` absent = enabled; `false` stops the one-shot hardware survey.
        desktop_electron_performance_report: z
            .object({
                enabled: z.boolean().optional(),
                sampleIntervalSeconds: z.number().optional(),
                perSurface: z.boolean().optional(),
                hardwareSurvey: z.boolean().optional(),
            })
            .optional(),
        // `encoders` is a comma-separated allow-list ("nvenc,amd,qsv"); absent = all hardware encoders
        desktop_capture_hw_encode: z
            .object({
                encoders: z.string().optional(),
            })
            .optional(),
        // Presence = the desktop app uploads the full installed-apps list again
        // every `intervalDays` (7 if absent). Absent = first-launch upload only.
        desktop_installed_apps_weekly: z
            .object({
                intervalDays: z.number().optional(),
            })
            .optional(),
        mod_suggestions: presenceFeatureFlag,
        sponsor_featured_content_2025_11_raccoon_logic: presenceFeatureFlag,
        tarkov_info: presenceFeatureFlag,
        game_session_tracking: presenceFeatureFlag,
        wemod_post_trainer_trustpilot_nps: z
            .object({
                wemod_percentage: z.number().optional(),
            })
            .optional(),
        game_assistant_embed: presenceFeatureFlag,
        game_assistant_free: presenceFeatureFlag,
        maps_compass: presenceFeatureFlag,
        maps_mini_map_cta: presenceFeatureFlag,
        nearby_pins: z
            .object({
                activeTitles: z.string().optional(),
            })
            .optional(),
        desktop_oauth: presenceFeatureFlag,
        desktop_rewarded_ads: presenceFeatureFlag.catch(undefined),
        labs_enabled: presenceFeatureFlag,
        // Standard intent-first First Look for eligible new users.
        desktop_first_look: presenceFeatureFlag,
        // One overlay auto-open on a new user's first overlay-supported game launch. `joinedAfter` (ISO
        // date) defines "new user": accounts created before it never qualify; absent = nobody does.
        desktop_overlay_first_launch_auto_open: z
            .object({
                joinedAfter: z.string().optional(),
            })
            .optional(),
        programmatic_ads_wand_com: presenceFeatureFlag,
    })
    .passthrough()
    .describe('FeatureFlags');

export type FeatureFlags = z.infer<typeof featureFlagsSchema>;
